import DatePicker from '@allesauto/core/datePicker';
import InputField from '@allesauto/core/inputField';
import RadioButton from '@allesauto/core/radioButton';
import SearchDropdown, { type Option } from '@allesauto/core/searchDropdown';
import Textarea from '@allesauto/core/textarea';
import { useWhiteLabel } from '@allesauto/core/whiteLabel';
import {
  CashOption,
  CommunicationChannel,
  SchemaTypes,
  TimeSlot,
} from '@allesauto/graphql';
import { InputMaybe, PaymentType } from '@allesauto/graphql/src/mock/schema';
import translate from '@allesauto/i18n';
import { pushAnalyticsEvent } from '@allesauto/utils/analytics';
import { SupportedLocales } from '@allesauto/utils/consts';
import {
  formatDateFormsRequest,
  getDealerAppointmentMinDate,
} from '@allesauto/utils/funcs';
import { isWhiteLabel } from '@allesauto/utils/funcs.ts';
import clsx from 'clsx';
import { useFormik } from 'formik';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { PreapprovalFormAnswers } from '@/components/forms/PreApprovalForm/CreditAssessmentOverlay';
import PrivacyNotice from '@/components/forms/PrivacyNotice/PrivacyNotice';
import { CustomerDetails } from '@/components/PDP/aboveFold/carDealerDetails/CarDealerDetails';
import ReCaptchaValidator, {
  ITokenValidator,
} from '@/components/ReCaptcha/ReCaptchaValidator';

import { useCarConfiguratorQueryParams } from '@/features/PDP/hooks';
import {
  sharedOverlayDetailsSteps,
  sharedOverlaySuccessTranslations,
} from '@/global/funcs/overlaySuccessSharedTranslations';
import { useContactDealerMutations } from '@/global/hooks/useContactDealerMutations';
import useNewsletterSubscribeCheckbox from '@/global/hooks/useNewsletterSubscribeCheckbox';
import {
  closeOverlay,
  openOverlay,
  setOverlayLoadingState,
} from '@/store/redux/actions/app';

import useTranslationContactDealer from './contactDealerTranslation';
import OverlaySuccess from '../overlaySuccess/OverlaySuccess';
import {
  allowedPhonePattern,
  phoneInputChange,
  phoneOnPasteSanitizer,
} from '../validation/formUtils';
import Footer from '../../OverlayFormFooter/OverlayFormFooter';

export interface ContactDealerFormProps {
  dealerId: string;
  vehicleSku: string;
  carModel: string | undefined | null;
  carManufacturer: string | undefined | null;
  dealerName: string;
  contactPerson: SchemaTypes.ContactPerson;
  financeCalculationResult?: SchemaTypes.FinanceCalculationResultInput;
  paymentType: SchemaTypes.PaymentType | null;
  darkBg?: boolean;
  onBackToSearchClick: () => void;
  customerDetails?: CustomerDetails;
  vehiclePrice: number;
  preApprovalFormContactDealerOverlay?: boolean;
  handleBackToPdpWhenPreapprovalFails?: () => void;
  submittedFormData?: {
    existingMonthlyInstallmentsSum: string;
    hasNoInsolvencyProceedings: string;
    hasNoNegativeCreditReports: string;
    isNotABusinessOwner: string;
    isVehiclePrivatelyUsed: string;
    monthlyNetIncome: string;
    otherMonthlyExpensesSum: string;
    peopleInHousehold: string;
    bankingType: SchemaTypes.PaymentType;
    finalInstallment: number;
    purchasePrice: number;
    term: number;
  };
  statusPreapprovalPassed?: boolean | undefined;
}

const ContactDealerForm: FC<
  React.PropsWithChildren<ContactDealerFormProps>
> = ({
  darkBg = false,
  carModel,
  carManufacturer,
  dealerId,
  dealerName,
  contactPerson,
  onBackToSearchClick,
  vehicleSku,
  customerDetails,
  financeCalculationResult,
  paymentType,
  vehiclePrice,
  preApprovalFormContactDealerOverlay,
  handleBackToPdpWhenPreapprovalFails,
  submittedFormData,
  statusPreapprovalPassed,
}) => {
  const { email, firstName, lastName, phone } = customerDetails ?? {};
  const intl = useIntl();
  const dispatch = useDispatch();
  const whitelabel = useWhiteLabel();
  const isWhiteLabelInstance = isWhiteLabel(whitelabel);
  const minimumDate = getDealerAppointmentMinDate(new Date(), 1);
  const { contactDealer } = useContactDealerMutations();
  const {
    memoizedContactDealerFormSchema,
    memoizedContactDealerFormTranslations,
  } = useTranslationContactDealer();
  const { carConfiguratorId } = useCarConfiguratorQueryParams();
  const tokenValidator = useRef<ITokenValidator | undefined>();

  const setTokenValidator = useCallback((v: ITokenValidator) => {
    tokenValidator.current = v;
  }, []);

  const overlayLoadingState = useSelector(
    (state: any) => state.app.overlayProps.isLoading
  );

  const userMessagePrefilled = translate(
    intl,
    !statusPreapprovalPassed && preApprovalFormContactDealerOverlay
      ? 'ui.FORMS.CONTACT_DEALER.USER_MSG_PREFILLED_PREAPPROVAL_FAILED'
      : 'ui.FORMS.CONTACT_DEALER.USER_MSG_PREFILLED',
    {
      car: `${carManufacturer} ${carModel}`,
    }
  );
  const className = {
    personalDetailsContainer: 'mb-[60px]',
    requestLabel: clsx(
      'font-weight-bold',
      !darkBg && ' text-grey100',
      darkBg && 'text-white'
    ),
    radioBtnsContainer:
      'grid grid-cols-2 gap-20px mt-10px md:flex lg:flex xl:flex',
    dateTimeWindowContainer:
      'mt-30px grid grid-cols-1 md:flex lg:flex xl:flex ',
    timeframeWindowContainer:
      'mt-30px md:flex-1 md:ml-40px md:mt-0 lg:flex-1 lg:ml-40px lg:mt-0 xl:flex-1 xl:ml-40px xl:mt-0 z-1',
    newsletterPrivacy: 'mt-60px',
    privacyNotice: clsx(
      'font-regular mt-30px text-p3',
      darkBg ? 'text-white' : 'text-grey60'
    ),
    requiredFieldNote: clsx(
      'font-regular mt-30px pb-10px text-p3',
      darkBg ? 'text-white' : 'text-grey80'
    ),
    error: 'text-red60 text-p3 mt-10px',
  };

  const memoizedOverlaySuccessTranslations = useMemo(() => {
    return sharedOverlaySuccessTranslations(
      intl,
      dealerName,
      isWhiteLabelInstance
    );
  }, [intl, dealerName, isWhiteLabelInstance]);

  const successOverlayDetailsSteps: React.ReactNode[] = useMemo(() => {
    return sharedOverlayDetailsSteps(
      intl,
      dealerName,
      contactPerson,
      isWhiteLabelInstance
    );
  }, [intl, dealerName, contactPerson, isWhiteLabelInstance]);

  const {
    NewsletterSubscribeCheckbox,
    checkboxProps,
    subscribe: subscribeToNewsletter,
  } = useNewsletterSubscribeCheckbox({ isDarkBg: darkBg });

  const [cashOption, setCashOption] = useState<
    InputMaybe<CashOption> | undefined
  >();

  useEffect(() => {
    if (paymentType === PaymentType.CashRange) {
      setCashOption(CashOption.Prepayment);
    } else {
      setCashOption(undefined);
    }
  }, [paymentType]);

  const formik = useFormik({
    initialValues: {
      userMsg: userMessagePrefilled,
      firstName: firstName ?? '',
      lastName: lastName ?? '',
      email: email ?? '',
      requestType: CommunicationChannel.Callback,
      phone: phone ?? '',
      requestDate: '',
      requestTimeWindow: '',
    },

    onSubmit: async (data) => {
      let token = '';

      if (process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA) {
        token = (await tokenValidator.current?.validate()) || '';
        if (!token) {
          return false;
        }
      }

      dispatch(setOverlayLoadingState(true));

      const formatedDate =
        data.requestDate !== '' && new Date(data.requestDate);

      const formData: SchemaTypes.ContactDealerInput = {
        contactMessage: data.userMsg || userMessagePrefilled,
        userFirstName: data.firstName,
        userLastName: data.lastName,
        userEmail: data.email,
        ...(data.phone && { userPhone: data.phone }),
        ...(financeCalculationResult &&
          financeCalculationResult.monthlyInstallment && {
            financeCalculationResult,
          }),
        paymentType,
        communicationChannel: data.requestType,
        ...(data.requestTimeWindow &&
          data.requestDate && {
            requestTimeSlotOption: {
              requestTimeSlot: data.requestTimeWindow as TimeSlot,
              requestDate: formatedDate
                ? formatDateFormsRequest(formatedDate)
                : '',
            },
          }),
        vehiclePrice,
      };

      if (submittedFormData) {
        formData.productBankingPreApprovalDetails = {
          bankingType: submittedFormData.bankingType,
          hasNoNegativeCreditReports:
            submittedFormData.hasNoNegativeCreditReports ===
            PreapprovalFormAnswers.Positive,
          isVehiclePrivatelyUsed:
            submittedFormData.isVehiclePrivatelyUsed ===
            PreapprovalFormAnswers.Positive,
          hasNoInsolvencyProceedings:
            submittedFormData.hasNoInsolvencyProceedings ===
            PreapprovalFormAnswers.Positive,
          isNotABusinessOwner:
            submittedFormData.isNotABusinessOwner ===
            PreapprovalFormAnswers.Positive,
          existingMonthlyInstallmentsSum: parseFloat(
            submittedFormData.existingMonthlyInstallmentsSum
          ),
          otherMonthlyExpensesSum: parseFloat(
            submittedFormData.otherMonthlyExpensesSum
          ),
          peopleInHousehold: parseInt(submittedFormData.peopleInHousehold, 10),
          monthlyNetIncome: parseFloat(submittedFormData.monthlyNetIncome),
          finalInstallment: submittedFormData.finalInstallment,
          purchasePrice: submittedFormData.purchasePrice,
          term: submittedFormData.term,
        };
      }

      if (cashOption) {
        formData.cashOption = cashOption;
      }

      try {
        const result = await contactDealer(
          vehicleSku,
          dealerId,
          formData,
          token,
          carConfiguratorId
        );

        pushAnalyticsEvent({
          event: 'dealer contact',
          contact_type: 'dealer',
          contact_dealer_action: result ? 'success' : 'start',
          contact_dealer_medium:
            formData.communicationChannel.toLocaleLowerCase(),
          dealer_name: dealerName.toLocaleLowerCase(),
        });

        subscribeToNewsletter({
          email: formData.userEmail,
          source:
            data.requestType === CommunicationChannel.Videocall
              ? 'SubscribeContactDealerVideo'
              : 'SubscribeContactDealerCommon',
        });

        dispatch(closeOverlay());

        setTimeout(
          () =>
            dispatch(
              openOverlay({
                children: (
                  // TODO: add headline, description and favorite button when ready
                  // bodyHeadline="My Headline"
                  // bodyDescription="My description"
                  // textLink={<TextLink onClick={onFavListClick} />}
                  <OverlaySuccess
                    bannerHeadline={
                      memoizedOverlaySuccessTranslations.SUBMISSION_THANK_YOU
                    }
                    bannerDescription={translate(
                      intl,
                      isWhiteLabel(whitelabel)
                        ? 'ui.FORMS.OVERLAY_SUCCESS.DEALER_CONFIRMATION_MSG_WL'
                        : memoizedOverlaySuccessTranslations.DEALER_CONFIRMATION_MSG
                    )}
                    detailsHeadline={
                      memoizedOverlaySuccessTranslations.NEXT_STEPS_TEXT
                    }
                    details={successOverlayDetailsSteps}
                    ctaLabel={
                      preApprovalFormContactDealerOverlay
                        ? memoizedOverlaySuccessTranslations.CREDIT_ASSESSMENT_BACK_TO_PDP
                        : memoizedOverlaySuccessTranslations.BUTTON_TEXT
                    }
                    onBtnClick={
                      preApprovalFormContactDealerOverlay
                        ? handleBackToPdpWhenPreapprovalFails
                        : onBackToSearchClick
                    }
                  />
                ),
                title: '',
                withPaddings: false,
              })
            ),
          100
        );
      } catch (e) {
        // the error is handled by the apollo errorHandler
        dispatch(setOverlayLoadingState(false));
      }
    },

    validationSchema: memoizedContactDealerFormSchema,
  });

  // TODO: TO BE DEFINED WHEN WE HAVE FAVORITE LIST
  // const onFavListClick = () => {
  // };

  const setTimeWindowField = (option: Option) => {
    formik.setFieldValue(
      'requestTimeWindow',
      !option ? undefined : option.value
    );
  };

  return (
    <form className="mx-[3px] pb-100px">
      <div className="h-full mb-20px">
        <div className={className.personalDetailsContainer}>
          <Textarea
            label={memoizedContactDealerFormTranslations.USER_MSG_LABEL}
            className="mt-20px"
            name="userMsg"
            value={formik.values.userMsg}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            darkBg={darkBg}
          />
          {formik.errors.userMsg ? (
            <div className={className.error}>{formik.errors.userMsg}</div>
          ) : (
            ''
          )}
          <InputField
            placeholder={`${memoizedContactDealerFormTranslations.FIRST_NAME}*`}
            errorMessage={
              formik.touched.firstName ? formik.errors.firstName : ''
            }
            name="firstName"
            customStyle="mt-30px"
            value={formik.values.firstName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            darkBg={darkBg}
          />
          <InputField
            placeholder={`${memoizedContactDealerFormTranslations.LAST_NAME}*`}
            errorMessage={formik.touched.lastName ? formik.errors.lastName : ''}
            customStyle="mt-30px"
            name="lastName"
            value={formik.values.lastName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            darkBg={darkBg}
          />

          <InputField
            type="email"
            errorMessage={formik.touched.email ? formik.errors.email : ''}
            placeholder={`${memoizedContactDealerFormTranslations.EMAIL_LABEL}*`}
            customStyle="mt-30px text-purple80"
            name="email"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            darkBg={darkBg}
          />
        </div>
        <div>
          <div className={className.requestLabel}>
            {memoizedContactDealerFormTranslations.REQUEST_LABEL}
          </div>
          <div className={className.error}>
            {!formik.values.requestType ? formik.errors.requestType : ''}
          </div>
          <div className={className.radioBtnsContainer} role="group">
            <RadioButton
              label={memoizedContactDealerFormTranslations.EMAIL_RADIO_LABEL}
              name="requestType"
              value={CommunicationChannel.Email}
              checked={formik.values.requestType === CommunicationChannel.Email}
              onChange={(value) => formik.setFieldValue('requestType', value)}
              darkBg={darkBg}
            />
            <div className="md:ml-40px lg:ml-40px xl:ml-40px">
              <RadioButton
                label={memoizedContactDealerFormTranslations.CALL_BACK_LABEL}
                name="requestType"
                value={CommunicationChannel.Callback}
                checked={
                  formik.values.requestType === CommunicationChannel.Callback
                }
                onChange={(value) => formik.setFieldValue('requestType', value)}
                darkBg={darkBg}
              />
            </div>
            <div className="md:ml-40px lg:ml-40px xl:ml-40px">
              <RadioButton
                label={memoizedContactDealerFormTranslations.VIDEO_CALL_LABEL}
                name="requestType"
                value={CommunicationChannel.Videocall}
                checked={
                  formik.values.requestType === CommunicationChannel.Videocall
                }
                onChange={(value) => formik.setFieldValue('requestType', value)}
                darkBg={darkBg}
              />
            </div>
          </div>
        </div>
        {formik.values.requestType === CommunicationChannel.Callback && (
          <InputField
            type="tel"
            errorMessage={formik.touched.phone ? formik.errors.phone : ''}
            label=""
            placeholder={`${memoizedContactDealerFormTranslations.PHONE}*`}
            customStyle="mt-30px"
            name="phone"
            value={formik.values.phone}
            onChange={(event: React.FormEvent) =>
              formik.setFieldValue('phone', phoneInputChange(event))
            }
            onPaste={(event: React.ClipboardEvent) =>
              formik.setFieldValue('phone', phoneOnPasteSanitizer(event))
            }
            onKeyPress={allowedPhonePattern}
            onBlur={formik.handleBlur}
            darkBg={darkBg}
          />
        )}
        {(formik.values.requestType === CommunicationChannel.Callback ||
          formik.values.requestType === CommunicationChannel.Videocall) && (
          <div className={className.dateTimeWindowContainer}>
            <div className="md:flex-1 lg:flex-1 xl:flex-1">
              <DatePicker
                name="requestDate"
                placeholderText={`${memoizedContactDealerFormTranslations.DATE_PLACEHOLDER_TEXT}*`}
                value={
                  formik.values.requestDate
                    ? formik.values.requestDate
                    : undefined
                }
                locale={(intl.locale || intl.defaultLocale) as SupportedLocales}
                onChange={(value) => formik.setFieldValue('requestDate', value)}
                onBlur={formik.handleBlur}
                darkBg={darkBg}
                errorState={
                  formik.touched.requestDate && !!formik.errors.requestDate
                }
                errorMessage={
                  formik.touched.requestDate && formik.errors.requestDate
                    ? formik.errors.requestDate
                    : ''
                }
                minDate={minimumDate}
              />
            </div>
            <div className={className.timeframeWindowContainer}>
              <SearchDropdown
                placeholder={`${memoizedContactDealerFormTranslations.TIMEWINDOW}*`}
                value={
                  !formik.values.requestTimeWindow
                    ? null
                    : {
                        value: formik.values.requestTimeWindow || '',
                        label:
                          translate(
                            intl,
                            `ui.FORMS.CONTACT_DEALER.TIMEWINDOW_LABELS.${formik.values.requestTimeWindow}`
                          ) || '',
                      }
                }
                options={[
                  {
                    value: TimeSlot.Morning,
                    label: translate(
                      intl,
                      'ui.FORMS.CONTACT_DEALER.TIMEWINDOW_LABELS.MORNING'
                    ),
                  },
                  {
                    value: TimeSlot.Noon,
                    label: translate(
                      intl,
                      'ui.FORMS.CONTACT_DEALER.TIMEWINDOW_LABELS.NOON'
                    ),
                  },
                  {
                    value: TimeSlot.Afternoon,
                    label: translate(
                      intl,
                      'ui.FORMS.CONTACT_DEALER.TIMEWINDOW_LABELS.AFTERNOON'
                    ),
                  },
                ]}
                isSearchable={false}
                hasLabelAnimation={false}
                onChange={(selected) => setTimeWindowField(selected as Option)}
                darkBg={darkBg}
                fieldRequired={
                  formik.touched.requestTimeWindow &&
                  !!formik.errors.requestTimeWindow
                }
                errorMessage={formik.errors.requestTimeWindow}
              />
            </div>
          </div>
        )}

        <div className={className.newsletterPrivacy}>
          {!statusPreapprovalPassed && preApprovalFormContactDealerOverlay ? (
            <>
              <p
                className={clsx(
                  'text-p3',
                  darkBg ? 'text-white' : 'text-grey60'
                )}
              >
                {translate(intl, 'ui.FORMS.NEWSLETTER.PARTIAL.AGREEMENT')}
              </p>
            </>
          ) : (
            <>
              {!whitelabel && (
                <NewsletterSubscribeCheckbox {...checkboxProps} />
              )}
            </>
          )}
          <PrivacyNotice
            translationKey="ui.FORMS.COMMON.PRIVACY_NOTICE.CONTACT_DEALER"
            className={className.privacyNotice}
          />
        </div>

        <div className={className.requiredFieldNote}>
          {memoizedContactDealerFormTranslations.REQUIRED_FIELD_NOTE}
        </div>
      </div>

      {process.env.NEXT_PUBLIC_FEATUREFLAG_RECAPTCHA && (
        <ReCaptchaValidator setStepValidator={setTokenValidator} />
      )}

      <Footer
        buttonText={memoizedContactDealerFormTranslations.BUTTON_TEXT}
        onButtonClick={formik.handleSubmit}
        isButtonDisabled={formik.isSubmitting}
        className="!z-50"
        showButtonLoader={overlayLoadingState || false}
      />
    </form>
  );
};

export default ContactDealerForm;
